export default function LegalPage() {
  const scrollToSection = (sectionId: string) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      const offsetPosition = elementPosition - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <section className="pt-12 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-5xl font-bold text-gray-900 mb-6">Legal Information</h1>
          <p className="text-lg text-gray-600 mb-8">
            Last Updated: January 1, 2026
          </p>

          <div className="flex gap-4 mb-12">
            <button
              onClick={() => scrollToSection('privacy-policy')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
            >
              Privacy Policy
            </button>
            <button
              onClick={() => scrollToSection('terms-of-service')}
              className="px-6 py-3 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 transition-colors shadow-md"
            >
              Terms of Service
            </button>
          </div>

          <div id="privacy-policy" className="bg-white rounded-lg shadow-lg p-8 mb-12 scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Privacy Policy</h2>

            <div className="space-y-6 text-gray-700">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Information We Collect</h3>
                <p className="mb-3">
                  We collect information that you provide directly to us, including:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name, email address, phone number, and property address</li>
                  <li>Property details and information you provide about your home</li>
                  <li>Communications you have with us</li>
                  <li>Any other information you choose to provide</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">How We Use Your Information</h3>
                <p className="mb-3">We use the information we collect to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Provide you with cash offers for your property</li>
                  <li>Communicate with you about our services</li>
                  <li>Process transactions and provide customer support</li>
                  <li>Improve our services and user experience</li>
                  <li>Comply with legal obligations</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Information Sharing</h3>
                <p className="mb-3">
                  We do not sell, rent, or share your personal information with third parties except:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>With your consent</li>
                  <li>To comply with legal obligations</li>
                  <li>With service providers who assist in our operations (under confidentiality agreements)</li>
                  <li>In connection with a business transaction such as a merger or sale of assets</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Data Security</h3>
                <p>
                  We implement reasonable security measures to protect your personal information. However, no method of transmission over the Internet or electronic storage is 100% secure, and we cannot guarantee absolute security.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Your Rights</h3>
                <p className="mb-3">You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your information</li>
                  <li>Opt-out of marketing communications</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Cookies and Tracking</h3>
                <p>
                  Our website may use cookies and similar tracking technologies to enhance user experience and analyze site traffic. You can control cookie settings through your browser.
                </p>
              </div>
            </div>
          </div>

          <div id="terms-of-service" className="bg-white rounded-lg shadow-lg p-8 scroll-mt-24">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Terms of Service</h2>

            <div className="space-y-6 text-gray-700">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Services</h3>
                <p className="mb-3">
                  Quick Close Home Offers is a direct home buyer operating nationwide. We are not a licensed real estate agent or broker. We provide cash offers to purchase residential properties directly from homeowners across the United States.
                </p>
                <p>
                  Our services are subject to availability in your area and compliance with all applicable federal, state, and local laws and regulations.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">No Obligation</h3>
                <p>
                  Requesting a cash offer from us creates no obligation to sell your property. You are free to accept or decline our offer at any time before closing.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Offers and Valuations</h3>
                <p className="mb-3">
                  All offers are based on property condition, market analysis, and other relevant factors. Offers are subject to:
                </p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Property inspection and verification of information provided</li>
                  <li>Clear title and no undisclosed liens or encumbrances</li>
                  <li>Mutual agreement on terms and closing date</li>
                </ul>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Accurate Information</h3>
                <p>
                  You agree to provide accurate and complete information about your property. Misrepresentation of property condition or other material facts may result in offer adjustment or withdrawal.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">As-Is Purchases</h3>
                <p>
                  We purchase properties in as-is condition. This means you are not required to make repairs or improvements. However, our offer reflects the current condition of the property.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Closing Process</h3>
                <p>
                  All transactions are completed through a licensed title company or attorney in accordance with the laws and regulations of the state where the property is located. Closing dates are mutually agreed upon and we work to accommodate your timeline.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Limitation of Liability</h3>
                <p>
                  Quick Close Home Offers and its affiliates shall not be liable for any indirect, incidental, special, or consequential damages arising from the use of our services or website.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Modifications</h3>
                <p>
                  We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting to our website. Your continued use of our services constitutes acceptance of modified terms.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Governing Law and Jurisdiction</h3>
                <p className="mb-3">
                  These terms shall be governed by and construed in accordance with the laws of the United States and the laws of the state in which the property is located, without regard to conflict of law provisions.
                </p>
                <p>
                  Any disputes arising from these terms or our services shall be resolved in the jurisdiction where the subject property is located, or where the transaction occurs.
                </p>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Contact Information</h3>
                <p className="mb-3">
                  For questions about these terms or our privacy practices, please contact us:
                </p>
                <ul className="space-y-2">
                  <li><strong>Email:</strong> quickclosehomeoffers@gmail.com</li>
                  <li><strong>Phone:</strong> 412-284-6681</li>
                </ul>
              </div>

              <div className="mt-8 pt-6 border-t border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-3">Acceptance of Terms</h3>
                <p>
                  By using our website or services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and Privacy Policy.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
